import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format, addDays, startOfDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import type { TFunction } from 'i18next';
import type { Service, TimeSlot } from '@minibook/shared';
import { getAvailability, getCalendarAvailability } from '../../api/bookingApi.js';
import type { ApiError } from '../../api/client.js';
import { CALENDAR_RANGE_DAYS, DEFAULT_TIMEZONE } from '../../constants/booking.js';
import { filterValidSlots, formatBookingSlotTime } from '../bookingSlotUtils.js';
import { devError } from '../../lib/devLog.js';

type CalendarCacheEntry = {
  availableDates: string[];
  occupancyByDate: Record<string, number>;
  requestId: string | null;
};

type UseBookingAvailabilityParams = {
  slug: string | null;
  masterTimeZone: string | undefined;
  language: string;
  t: TFunction;
  selectedServices: Service[];
  currentPage: number;
  step: number;
  setError: (message: string | null) => void;
};

export function useBookingAvailability({
  slug,
  masterTimeZone,
  language,
  t,
  selectedServices,
  currentPage,
  step,
  setError,
}: UseBookingAvailabilityParams) {
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [availabilityRefreshCounter, setAvailabilityRefreshCounter] = useState(0);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState<Set<string> | null>(null);
  const [occupancyByDate, setOccupancyByDate] = useState<Record<string, number>>({});
  const [calendarRange, setCalendarRange] = useState<{ from: string; to: string } | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [calendarRequestId, setCalendarRequestId] = useState<string | null>(null);
  const [calendarRevealed, setCalendarRevealed] = useState(false);

  const slotsCacheRef = useRef<Record<string, TimeSlot[]>>({});
  const slotsAbortControllerRef = useRef<AbortController | null>(null);
  const lastSlotsRequestDateRef = useRef<string | null>(null);
  const prefetchingDatesRef = useRef<Set<string>>(new Set());
  const calendarAvailabilityCacheRef = useRef<Record<string, CalendarCacheEntry>>({});

  const formatDateKey = useCallback((date: Date) => date.toISOString().slice(0, 10), []);

  const parseDateKeyAtUtcNoon = useCallback((dateKey: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  }, []);

  const serviceIds = useMemo(() => selectedServices.map((s) => s.id).join(','), [selectedServices]);
  const serviceIdsArray = useMemo(
    () => (serviceIds ? serviceIds.split(',').filter(Boolean) : []),
    [serviceIds]
  );
  const slotsCacheKeySuffix = serviceIdsArray.length ? serviceIdsArray.join(',') : 'df';

  const masterTz = masterTimeZone || DEFAULT_TIMEZONE;
  const today = useMemo(
    () => format(utcToZonedTime(new Date(), masterTz), 'yyyy-MM-dd'),
    [masterTz]
  );
  const maxDate = useMemo(() => {
    const zonedNow = utcToZonedTime(new Date(), masterTz);
    return format(addDays(startOfDay(zonedNow), CALENDAR_RANGE_DAYS), 'yyyy-MM-dd');
  }, [masterTz]);

  const resetAvailabilityCaches = useCallback(() => {
    setSelectedDate('');
    setSelectedSlot(null);
    setAvailableSlots([]);
    setAvailableDates(null);
    setOccupancyByDate({});
    setCalendarRequestId(null);
    slotsCacheRef.current = {};
    calendarAvailabilityCacheRef.current = {};
    prefetchingDatesRef.current.clear();
    lastSlotsRequestDateRef.current = null;
    setAvailabilityRefreshCounter((value) => value + 1);
  }, []);

  const refreshAvailability = useCallback(() => {
    setAvailabilityRefreshCounter((value) => value + 1);
  }, []);

  const handleCalendarMonthChange = useCallback((_range: { from: string; to: string }) => {}, []);

  const handleDateSelect = useCallback(
    (date: string) => {
      if (date === selectedDate) return;
      setSelectedDate(date);
      setSelectedSlot(null);
      setError(null);
    },
    [selectedDate, setError]
  );

  const handleSlotSelect = useCallback((slot: TimeSlot) => {
    setSelectedSlot(slot);
  }, []);

  const formatSlotTime = useCallback(
    (isoString: string | undefined | null) => formatBookingSlotTime(isoString, masterTimeZone, language),
    [language, masterTimeZone]
  );

  useEffect(() => {
    if (!slug || calendarRange) return;
    const tz = masterTimeZone || DEFAULT_TIMEZONE;
    const zonedNow = utcToZonedTime(new Date(), tz);
    const fromDate = startOfDay(zonedNow);
    const toDate = addDays(fromDate, CALENDAR_RANGE_DAYS);
    setCalendarRange({
      from: format(fromDate, 'yyyy-MM-dd'),
      to: format(toDate, 'yyyy-MM-dd'),
    });
  }, [slug, calendarRange, masterTimeZone]);

  useEffect(() => {
    if (currentPage !== 2 || step !== 2) {
      setCalendarRevealed(false);
      return;
    }
    let cancelled = false;
    let innerId: number | undefined;
    const outerId = requestAnimationFrame(() => {
      innerId = requestAnimationFrame(() => {
        if (!cancelled) setCalendarRevealed(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(outerId);
      if (innerId !== undefined) cancelAnimationFrame(innerId);
    };
  }, [currentPage, step]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshAvailability();
    };
    window.addEventListener('focus', handleVisibility);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleVisibility);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refreshAvailability]);

  const prefetchNeighborSlots = useCallback(
    (baseDate: string, offset: number) => {
      if (!slug) return;
      const date = parseDateKeyAtUtcNoon(baseDate);
      if (!date) return;
      date.setUTCDate(date.getUTCDate() + offset);
      const neighborDate = formatDateKey(date);
      if (!neighborDate) return;
      const cacheKey = `${neighborDate}:${slotsCacheKeySuffix}`;
      if (slotsCacheRef.current[cacheKey] || prefetchingDatesRef.current.has(cacheKey)) return;
      prefetchingDatesRef.current.add(cacheKey);
      getAvailability(slug, { serviceIds: serviceIdsArray, date: neighborDate })
        .then((response) => {
          slotsCacheRef.current[cacheKey] = filterValidSlots(response.slots ?? []);
        })
        .catch((err: unknown) => {
          if ((err as Error).name === 'AbortError') return;
          devError('[BookingPage] Предзагрузка слотов не удалась:', err);
        })
        .finally(() => prefetchingDatesRef.current.delete(cacheKey));
    },
    [slug, serviceIdsArray, slotsCacheKeySuffix, formatDateKey, parseDateKeyAtUtcNoon]
  );

  useEffect(() => {
    if (currentPage !== 2 || step !== 2 || !selectedDate || !slug) return;

    if (!/^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
      devError('[BookingPage] Неверный формат даты:', selectedDate);
      setError(t('booking.error.invalidDate'));
      return;
    }

    const cacheKey = `${selectedDate}:${slotsCacheKeySuffix}`;
    const slotsCache = slotsCacheRef.current;
    if (Object.prototype.hasOwnProperty.call(slotsCache, cacheKey)) {
      setAvailableSlots(slotsCache[cacheKey]);
      setSlotsLoading(false);
      setError(null);
      lastSlotsRequestDateRef.current = selectedDate;
      prefetchNeighborSlots(selectedDate, -1);
      prefetchNeighborSlots(selectedDate, 1);
      return () => {};
    }

    setAvailableSlots([]);
    setSlotsLoading(true);
    setError(null);
    lastSlotsRequestDateRef.current = selectedDate;

    const controller = new AbortController();
    slotsAbortControllerRef.current?.abort();
    slotsAbortControllerRef.current = controller;

    getAvailability(slug, { serviceIds: serviceIdsArray, date: selectedDate }, { signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;
        if (response.reason === 'no_online_services' && response.slots.length === 0) {
          slotsCacheRef.current[cacheKey] = [];
          if (lastSlotsRequestDateRef.current === selectedDate) setAvailableSlots([]);
          prefetchNeighborSlots(selectedDate, -1);
          prefetchNeighborSlots(selectedDate, 1);
          return;
        }
        const validSlots = filterValidSlots(response.slots);
        if (validSlots.length !== response.slots.length) {
          devError('[BookingPage] Некорректные данные слотов', { slots: response.slots });
          setError(t('booking.error.serverData'));
        }
        slotsCacheRef.current[cacheKey] = validSlots;
        if (lastSlotsRequestDateRef.current === selectedDate) setAvailableSlots(validSlots);
        prefetchNeighborSlots(selectedDate, -1);
        prefetchNeighborSlots(selectedDate, 1);
      })
      .catch((err: ApiError) => {
        if ((err as unknown as Error)?.name === 'AbortError') return;
        devError('[BookingPage] Ошибка загрузки слотов:', err);
        if (lastSlotsRequestDateRef.current === selectedDate) {
          setError(err.error?.message || t('booking.error.loadSlots'));
        }
      })
      .finally(() => {
        if (lastSlotsRequestDateRef.current === selectedDate) setSlotsLoading(false);
      });

    return () => controller.abort();
  }, [
    currentPage,
    step,
    selectedDate,
    slug,
    t,
    serviceIdsArray,
    slotsCacheKeySuffix,
    prefetchNeighborSlots,
    availabilityRefreshCounter,
    setError,
  ]);

  const buildCalendarCacheKey = useCallback(
    (from: string, services: string[], refresh: number) =>
      `${slug}_${from.slice(0, 7)}_${[...services].sort().join(',')}_${refresh}`,
    [slug]
  );

  useEffect(() => {
    if (!slug || !calendarRange) return;

    const cacheKey = buildCalendarCacheKey(calendarRange.from, serviceIdsArray, availabilityRefreshCounter);
    const cached = calendarAvailabilityCacheRef.current[cacheKey];
    if (cached) {
      setAvailableDates(new Set(cached.availableDates));
      setOccupancyByDate(cached.occupancyByDate);
      setCalendarRequestId(cached.requestId);
      setCalendarError(null);
      setCalendarLoading(false);
      return;
    }

    setCalendarLoading(true);
    setCalendarError(null);
    setCalendarRequestId(null);
    setAvailableDates(null);

    const rangeFrom = calendarRange.from;
    const rangeTo = calendarRange.to;

    (async () => {
      try {
        const response = await getCalendarAvailability(slug, {
          from: rangeFrom,
          to: rangeTo,
          serviceIds: serviceIdsArray,
        });
        const dates = response.availableDates ?? [];
        const occupancy = response.occupancyByDate ?? {};
        calendarAvailabilityCacheRef.current[cacheKey] = {
          availableDates: dates,
          occupancyByDate: occupancy,
          requestId: response.requestId ?? null,
        };
        setAvailableDates(new Set(dates));
        setOccupancyByDate(occupancy);
        setCalendarRequestId(response.requestId ?? null);

        const fromDate = new Date(rangeFrom);
        for (const monthsDelta of [-1, 1]) {
          const neighbor = new Date(fromDate.getFullYear(), fromDate.getMonth() + monthsDelta, 1);
          const neighborFrom = format(neighbor, 'yyyy-MM-dd');
          const neighborTo = format(addDays(neighbor, CALENDAR_RANGE_DAYS), 'yyyy-MM-dd');
          const neighborKey = buildCalendarCacheKey(neighborFrom, serviceIdsArray, availabilityRefreshCounter);
          if (calendarAvailabilityCacheRef.current[neighborKey]) continue;
          getCalendarAvailability(slug, { from: neighborFrom, to: neighborTo, serviceIds: serviceIdsArray })
            .then((res) => {
              calendarAvailabilityCacheRef.current[neighborKey] = {
                availableDates: res.availableDates ?? [],
                occupancyByDate: res.occupancyByDate ?? {},
                requestId: res.requestId ?? null,
              };
            })
            .catch(() => {});
        }
      } catch (err) {
        const apiError = err as ApiError;
        devError('[BookingPage] Failed to load calendar availability:', {
          error: apiError,
          requestId: apiError.requestId,
        });
        setAvailableDates(null);
        setOccupancyByDate({});
        setCalendarError(apiError.error?.message || t('booking.error.serverData'));
        setCalendarRequestId(apiError.requestId ?? null);
      } finally {
        setCalendarLoading(false);
      }
    })();
  }, [slug, calendarRange, serviceIdsArray, t, availabilityRefreshCounter, buildCalendarCacheKey]);

  useEffect(() => {
    if (!availableDates || availableDates.size === 0) return;
    const futureDates = Array.from(availableDates).filter((date) => date >= today).sort();
    const fallbackDates = Array.from(availableDates).sort();
    const nearestDate = futureDates[0] ?? fallbackDates[0];
    if (!nearestDate) return;
    if (selectedDate && availableDates.has(selectedDate)) return;
    if (selectedDate === nearestDate) return;
    setSelectedDate(nearestDate);
    setSelectedSlot(null);
  }, [availableDates, selectedDate, today]);

  useEffect(() => {
    if (!slug || !availableDates || availableDates.size === 0) return;
    const futureDates = Array.from(availableDates).filter((d) => d >= today).sort();
    const firstDate = futureDates[0] ?? Array.from(availableDates).sort()[0];
    if (!firstDate) return;
    const cacheKey = `${firstDate}:${slotsCacheKeySuffix}`;
    if (slotsCacheRef.current[cacheKey] || prefetchingDatesRef.current.has(cacheKey)) return;
    prefetchingDatesRef.current.add(cacheKey);
    getAvailability(slug, { serviceIds: serviceIdsArray, date: firstDate })
      .then((response) => {
        slotsCacheRef.current[cacheKey] = filterValidSlots(response.slots ?? []);
      })
      .catch(() => {})
      .finally(() => prefetchingDatesRef.current.delete(cacheKey));
  }, [slug, availableDates, today, serviceIdsArray, slotsCacheKeySuffix]);

  const groupedSlots = useMemo(() => {
    if (availableSlots.length === 0) return { morning: [], day: [], evening: [] };
    const morning: TimeSlot[] = [];
    const day: TimeSlot[] = [];
    const evening: TimeSlot[] = [];
    availableSlots.forEach((slot) => {
      let hours = new Date(slot.startAt).getHours();
      try {
        hours = utcToZonedTime(new Date(slot.startAt), masterTz).getHours();
      } catch {
        // fallback
      }
      if (hours < 12) morning.push(slot);
      else if (hours < 18) day.push(slot);
      else evening.push(slot);
    });
    const sortByTime = (a: { startAt: string }, b: { startAt: string }) =>
      new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
    morning.sort(sortByTime);
    day.sort(sortByTime);
    evening.sort(sortByTime);
    return { morning, day, evening };
  }, [availableSlots, masterTz]);

  return {
    selectedDate,
    setSelectedDate,
    selectedSlot,
    setSelectedSlot,
    availableSlots,
    slotsLoading,
    availableDates,
    occupancyByDate,
    calendarLoading,
    calendarError,
    calendarRequestId,
    calendarRevealed,
    today,
    maxDate,
    groupedSlots,
    formatSlotTime,
    handleDateSelect,
    handleSlotSelect,
    handleCalendarMonthChange,
    resetAvailabilityCaches,
    refreshAvailability,
    selectedSlotMaxMinutes: selectedSlot?.maxDurationMin ?? null,
  };
}
